import { Alert, styled } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useExternalSubmissionState } from '../../context/ExternalSubmissionContext';
import { useGetFeedbackForm } from '../../services/externalSubmissionQueries';

const CustomAlert = styled(Alert)(({ theme }) => ({ marginBottom: theme.spacing(2) }));

const CustomAlertMessage = () => {
	const { t } = useTranslation('biz-collab-alt-feedback-spa');
	const alertRef = useRef(null);
	const [alertContent, setAlertContent] = useState(null);
	const [showAlert, setShowAlert] = useState(false);

	const {
		isLoading: isFormLoading,
		isError: isFormError,
		data: { feedbackRequestStatus } = {},
		error: formError,
	} = useGetFeedbackForm();

	const { isSubmitting, isSubmitSuccess, isSubmitError, isSaving, isSaveSuccess, isSaveError } =
		useExternalSubmissionState();

	// eslint-disable-next-line complexity
	useEffect(() => {
		if (isFormLoading) {
			setAlertContent({
				severity: 'info',
				message: t('Please wait while we load your form.', { keySeparator: false }),
				testId: 'feedback-form-loading',
			});
			setShowAlert(true);
		} else if (isFormError) {
			if (
				formError?.response?.data?.message?.includes(
					'Ratings and Recommendations feature is not enabled',
				)
			) {
				setAlertContent({
					severity: 'error',
					message: t('This form is currently unavailable.', { keySeparator: false }),
					testId: 'feedback-form-error-currently-unavailable',
				});
			} else {
				setAlertContent({
					severity: 'error',
					message: t('This url is not valid anymore.', { keySeparator: false }),
					testId: 'feedback-form-error',
				});
			}
			setShowAlert(true);
		} else if (feedbackRequestStatus === 'CANCELLED') {
			setAlertContent({
				severity: 'error',
				message: t('The interview associated with this feedback request was canceled.', {
					keySeparator: false,
				}),
				testId: 'feedback-form-cancelled',
			});
			setShowAlert(true);
		} else if (isSubmitting) {
			setAlertContent({
				severity: 'info',
				message: t('Please wait while we submit your responses.', { keySeparator: false }),
				testId: 'feedback-form-submitting',
			});
			setShowAlert(true);
		} else if (isSubmitSuccess) {
			setAlertContent({
				severity: 'success',
				message: t('Thank you for submitting your feedback!', { keySeparator: false }),
				testId: 'feedback-form-submit-success',
			});
			setShowAlert(true);
		} else if (isSubmitError) {
			setAlertContent({
				severity: 'error',
				message: t('We were unable to submit your responses. Please try again.', {
					keySeparator: false,
				}),
				testId: 'feedback-form-submit-error',
			});
			setShowAlert(true);
		} else if (isSaving) {
			setAlertContent({
				severity: 'info',
				message: t('Please wait while we save your responses.', { keySeparator: false }),
				testId: 'feedback-form-saving',
			});
			setShowAlert(true);
		} else if (isSaveSuccess) {
			// Ensure the previous alert is removed before showing the success alert
			setShowAlert(false);
			setTimeout(() => {
				setAlertContent({
					severity: 'success',
					message: t('Thank you for saving your feedback! Please submit when ready.', {
						keySeparator: false,
					}),
					testId: 'feedback-form-save-success',
				});
				setShowAlert(true);
				// Use a minimal timeout to ensure the previous alert is removed
			}, 0);
		} else if (isSaveError) {
			setAlertContent({
				severity: 'error',
				message: t('We were unable to save your responses. Please try again.', {
					keySeparator: false,
				}),
				testId: 'feedback-form-save-error',
			});
			setShowAlert(true);
		} else {
			setShowAlert(false);
		}
	}, [
		isFormLoading,
		isFormError,
		isSubmitting,
		isSubmitSuccess,
		isSubmitError,
		isSaving,
		isSaveSuccess,
		isSaveError,
		feedbackRequestStatus,
		formError,
		t,
	]);

	useEffect(() => {
		if (alertRef.current) {
			alertRef.current.focus();
		}
	}, [alertContent, showAlert]);

	if (!showAlert || !alertContent) {
		return null;
	}

	return (
		<CustomAlert
			ref={alertRef}
			role="alert"
			aria-live="assertive"
			severity={alertContent.severity}
			data-testid={alertContent.testId}
			tabIndex="-1"
		>
			{alertContent.message}
		</CustomAlert>
	);
};

export default CustomAlertMessage;
